'use client';

import type { ReactNode } from 'react';
import { SignalsRenderer, parseSignalData } from './SignalsRenderer';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import {
  parseSignalsListData,
  parseSignalsSearchData,
} from './SignalsRenderer';
import type { Signal } from './types';
import type { OutputRenderContext } from '../../tool-config-types';

const parseSignalsFromMarkdown = (content: string): Signal[] | null => {
  const itemPattern =
    /-\s+\*\*(.*?)\*\*\s*\n\s*ID:\s*([^\n]+)(?:\n\s*Label:\s*([^|]+)\|\s*Category:\s*([^\n]+))?(?:\n\s*Confidence:\s*([^\n]+))?(?:\n\s*Endpoint:\s*(?:(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD)\s+)?([^\n]+))?/gi;
  const signals: Signal[] = [];

  for (const match of content.matchAll(itemPattern)) {
    signals.push({
      title: match[1]?.trim(),
      signalId: match[2]?.trim(),
      label: match[3]?.trim() as Signal['label'],
      category: match[4]?.trim(),
      confidence: match[5] ? Number(match[5]) : undefined,
      method: match[6]?.trim(),
      url: match[7]?.trim(),
    });
  }

  return signals.length > 0 ? signals : null;
};

const parseSignalFromMarkdown = (content: string): Signal | null => {
  const idMatch = content.match(/\bID:\s*([a-f0-9]{24})/i);
  const titleMatch = content.match(/## Signal Details\s*\n\*\*(.*?)\*\*/i);
  const labelMatch = content.match(/\bLabel:\s*([^|\n]+)/i);
  const categoryMatch = content.match(/\bCategory:\s*([^\n]+)/i);
  const confidenceMatch = content.match(/\bConfidence:\s*([^\n]+)/i);
  const endpointMatch = content.match(/\bEndpoint ID:\s*([a-f0-9]{24})/i);

  if (!idMatch && !titleMatch) {
    return null;
  }

  return {
    signalId: idMatch?.[1],
    title: titleMatch?.[1]?.trim(),
    label: labelMatch?.[1]?.trim() as Signal['label'],
    category: categoryMatch?.[1]?.trim(),
    confidence: confidenceMatch ? Number(confidenceMatch[1]) : undefined,
    endpointId: endpointMatch?.[1],
  };
};

export const renderSignalsListResult = (
  ctx: OutputRenderContext,
): ReactNode => {
  const { content, maxHeight, onOpenObject } = ctx;

  if (typeof content === 'object' && content !== null) {
    const signalsSearch = parseSignalsSearchData(content);
    if (signalsSearch && signalsSearch.signals.length > 0) {
      return (
        <SignalsRenderer
          signals={signalsSearch.signals}
          query={signalsSearch.query}
          totalResults={signalsSearch.totalResults}
          scoreThreshold={signalsSearch.scoreThreshold}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }

    const signals = parseSignalsListData(content);
    if (signals && signals.length > 0) {
      return (
        <SignalsRenderer
          signals={signals}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }
  } else if (typeof content === 'string') {
    const markdownSignals = parseSignalsFromMarkdown(content);
    if (markdownSignals) {
      return (
        <SignalsRenderer
          signals={markdownSignals}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }

    try {
      const parsed = JSON.parse(content);
      const signalsSearch = parseSignalsSearchData(parsed);
      if (signalsSearch && signalsSearch.signals.length > 0) {
        return (
          <SignalsRenderer
            signals={signalsSearch.signals}
            query={signalsSearch.query}
            totalResults={signalsSearch.totalResults}
            scoreThreshold={signalsSearch.scoreThreshold}
            maxHeight={maxHeight}
            onOpenObject={onOpenObject}
          />
        );
      }

      const signals = parseSignalsListData(parsed);
      if (signals && signals.length > 0) {
        return (
          <SignalsRenderer
            signals={signals}
            maxHeight={maxHeight}
            onOpenObject={onOpenObject}
          />
        );
      }
    } catch {
      // Fall through to JSON renderer
    }
  }

  return <JSONRenderer data={content} maxHeight={maxHeight} />;
};

export const renderSignalResult = (ctx: OutputRenderContext): ReactNode => {
  const { content, input, maxHeight, onOpenObject } = ctx;

  if (input && typeof input === 'object') {
    const signal = parseSignalData(input as Partial<Signal>);
    if (signal) {
      return (
        <SignalsRenderer
          signals={[signal]}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }
  }

  if (typeof content === 'string') {
    const signal = parseSignalFromMarkdown(content);
    if (signal) {
      return (
        <SignalsRenderer
          signals={[signal]}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }
  }

  if (typeof content === 'object' && content !== null) {
    const signal = parseSignalData(content as Partial<Signal>);
    if (signal) {
      return (
        <SignalsRenderer
          signals={[signal]}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }
  }

  return <JSONRenderer data={content} />;
};
